import { useState } from "react";
import {
  Server,
  Mail,
  Shield,
  Clock,
  Activity,
  Calendar,
  AlertTriangle,
  Building,
  Copy,
  CheckCircle,
  XCircle,
  UserCog,
  Cloud,
  MapPin,
  Fingerprint,
  Network,
  ChevronDown,
  Globe,
  Key,
  FileText,
} from "lucide-react";
import { Badge } from "../../../ui/common-ui/badge";
import { Separator } from "../../../ui/common-ui/separator";
import { Button } from "../../../ui/common-ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../../../ui/common-ui/collapsible";
import { GraphNode } from "../types/graph";

interface ServiceAccountInspectorProps {
  node: GraphNode;
  hideIdentityHeader?: boolean;
}

// Format date to readable format
const formatDate = (dateString: string | undefined) => {
  if (!dateString) return "N/A";
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

// Get activity level color
const getActivityColor = (activity: number) => {
  if (activity >= 80) return "text-destructive";
  if (activity >= 50) return "text-primary";
  if (activity >= 20) return "text-muted-foreground";
  return "text-muted-foreground/50";
};

export function ServiceAccountInspector({ node, hideIdentityHeader = false }: ServiceAccountInspectorProps) {
  const props = node.props || {};
  const [regionsOpen, setRegionsOpen] = useState(false);

  // Determine cloud type
  const isAzure = props.cloudType === 'AZURE';
  const isAws = props.cloudType === 'AWS';

  // Common fields - for Azure use name, for AWS use roleName
  const email = props.serviceAccountEmail || props.name || props.roleArn || props.email || "";
  const saName = isAws
    ? (props.roleName || node.label)
    : isAzure
      ? (props.name || node.label)
      : (email.split('@')[0] || node.label);
  const roles = props.roles || [];
  const roleNames: Record<string, string> = props.roleNames || {};
  const syncedAt = props.syncedAt;
  const projectId = props.projectId;
  const tenantName = props.tenantName;
  const lastActivity = props.lastActivity;
  const isDormant = props.isDormant;
  const isUserManaged = props.isUserManaged;
  const activity = props.activity || 0;
  const lastActivityMessage = props.lastActivityMessage || "";
  const roleCount = props.roleCount || roles.length || 0;
  const resourceCount = props.resourceCount || 0;
  const alertCount = props.alertCount || 0;

  // Azure-specific fields for Managed Identity
  const resourceGroup = props.resourceGroup;
  const region = props.region;
  const identityType = props.identityType;
  const principalId = props.principalId;
  const subscriptionId = props.subscriptionId;

  // AWS-specific fields for IAM Role
  const roleArn = props.roleArn;
  const accountId = props.accountId;
  const path = props.path;
  // Ensure regions is always an array
  const regionsRaw = props.regions;
  const regions: string[] = Array.isArray(regionsRaw) ? regionsRaw : [];
  const description = props.description;
  const createDate = props.createDate;
  const lastUsedAt = props.lastUsedAt;
  const maxSessionDuration = props.maxSessionDuration;
  const inlinePolicyNames: string[] = Array.isArray(props.inlinePolicyNames) ? props.inlinePolicyNames : [];

  return (
    <div className="space-y-4">
      {!hideIdentityHeader && (
        <>
          {/* Service Account / Managed Identity Header */}
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-purple-500/10 flex items-center justify-center border border-purple-500/20">
                <Server className="w-6 h-6 text-purple-500" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-lg font-semibold text-foreground leading-tight truncate">
                  {saName}
                </h3>
                <div className="flex flex-wrap gap-1.5 mt-1">
                  {isDormant !== undefined && (
                    <Badge
                      variant="outline"
                      className={`text-[10px] ${isDormant
                        ? "border-destructive/40 bg-destructive/10 text-destructive"
                        : "border-primary/30 bg-primary/10 text-primary"
                        }`}
                    >
                      {isDormant ? (
                        <>
                          <XCircle className="w-3 h-3 mr-1" />
                          Dormant
                        </>
                      ) : (
                        <>
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Active
                        </>
                      )}
                    </Badge>
                  )}
                  {/* For GCP: User Managed / Cloud Managed Badge */}
                  {!isAzure && !isAws && isUserManaged !== undefined && (
                    <Badge
                      variant="outline"
                      className={`text-[10px] ${isUserManaged
                        ? "border-blue-500/40 bg-blue-500/10 text-blue-600 dark:text-blue-400"
                        : "border-purple-500/40 bg-purple-500/10 text-purple-600 dark:text-purple-400"
                        }`}
                    >
                      {isUserManaged ? (
                        <>
                          <UserCog className="w-3 h-3 mr-1" />
                          User Managed
                        </>
                      ) : (
                        <>
                          <Cloud className="w-3 h-3 mr-1" />
                          Cloud Managed
                        </>
                      )}
                    </Badge>
                  )}
                  {/* For Azure: Show identity type badge */}
                  {isAzure && identityType && (
                    <Badge
                      variant="outline"
                      className="text-[10px] border-blue-500/40 bg-blue-500/10 text-blue-600 dark:text-blue-400"
                    >
                      <Fingerprint className="w-3 h-3 mr-1" />
                      Managed Identity
                    </Badge>
                  )}
                  {/* For AWS: Show IAM Role badge */}
                  {isAws && (
                    <Badge
                      variant="outline"
                      className="text-[10px] border-orange-500/40 bg-orange-500/10 text-orange-600 dark:text-orange-400"
                    >
                      <Key className="w-3 h-3 mr-1" />
                      IAM Role
                    </Badge>
                  )}
                </div>
              </div>
            </div>
          </div>

          <Separator className="bg-border/50" />

          {/* Stats Badges */}
          <div className="flex flex-wrap gap-2">
            {alertCount > 0 && (
              <Badge variant="outline" className="text-xs border-destructive/40 bg-destructive/10 text-destructive">
                <AlertTriangle className="w-3 h-3 mr-1" />
                {alertCount} Alert{alertCount !== 1 ? "s" : ""}
              </Badge>
            )}
            <Badge variant="outline" className="text-xs">
              <Shield className="w-3 h-3 mr-1" />
              {roleCount} Role{roleCount !== 1 ? "s" : ""}
            </Badge>
            <Badge variant="outline" className="text-xs">
              <Server className="w-3 h-3 mr-1" />
              {resourceCount} Resource{resourceCount !== 1 ? "s" : ""}
            </Badge>
          </div>

          <Separator className="bg-border/50" />
        </>
      )}

      {/* Service Account Email (GCP) or Name (Azure) */}
      {email && !isAzure && !isAws && (
        <div className="space-y-1.5">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
            Service Account Email
          </p>
          <div className="flex items-center gap-2">
            <Mail className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
            <span className="text-xs font-medium text-foreground break-all flex-1">
              {email}
            </span>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 hover:bg-muted/50 shrink-0"
              onClick={() => navigator.clipboard.writeText(email)}
              aria-label="Copy email"
            >
              <Copy className="w-3 h-3" />
            </Button>
          </div>
        </div>
      )}

      {/* AWS IAM Role specific fields */}
      {isAws && (
        <>
          {roleArn && (
            <div className="space-y-1.5">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                Role ARN
              </p>
              <div className="flex items-center gap-2">
                <Key className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                <span className="text-xs font-mono bg-muted/50 px-2 py-0.5 rounded border border-border/30 break-all flex-1">
                  {roleArn}
                </span>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 hover:bg-muted/50 shrink-0"
                  onClick={() => navigator.clipboard.writeText(roleArn)}
                  aria-label="Copy ARN"
                >
                  <Copy className="w-3 h-3" />
                </Button>
              </div>
            </div>
          )}

          {accountId && (
            <div className="space-y-1.5">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                Account ID
              </p>
              <div className="flex items-center gap-2">
                <Building className="w-3.5 h-3.5 text-muted-foreground" />
                <span className="text-xs font-mono bg-muted/50 px-2 py-0.5 rounded border border-border/30">
                  {accountId}
                </span>
              </div>
            </div>
          )}

          {/* Regions Dropdown */}
          {regions.length > 0 && (
            <Collapsible open={regionsOpen} onOpenChange={setRegionsOpen}>
              <div className="space-y-1.5">
                <CollapsibleTrigger className="flex items-center justify-between w-full">
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                    Regions ({regions.length})
                  </p>
                  <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform ${regionsOpen ? "rotate-180" : ""}`} />
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <div className="max-h-40 overflow-y-auto scrollbar-hidden space-y-1 p-2 rounded-lg border border-border/40 bg-muted/20">
                    {regions.map((r: string, idx: number) => (
                      <div key={idx} className="flex items-center gap-2 text-xs">
                        <Globe className="w-3 h-3 text-muted-foreground shrink-0" />
                        <span className="font-mono text-foreground">{r}</span>
                      </div>
                    ))}
                  </div>
                </CollapsibleContent>
              </div>
            </Collapsible>
          )}

          {description && (
            <div className="space-y-1.5">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                Description
              </p>
              <div className="flex items-start gap-2">
                <FileText className="w-3.5 h-3.5 text-muted-foreground shrink-0 mt-0.5" />
                <span className="text-xs text-foreground">
                  {description || "No description"}
                </span>
              </div>
            </div>
          )}

          {createDate && (
            <div className="space-y-1.5">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                Created
              </p>
              <div className="flex items-center gap-2">
                <Calendar className="w-3.5 h-3.5 text-muted-foreground" />
                <span className="text-xs font-medium text-foreground">
                  {formatDate(createDate)}
                </span>
              </div>
            </div>
          )}

          {maxSessionDuration && (
            <div className="space-y-1.5">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                Max Session Duration
              </p>
              <div className="flex items-center gap-2">
                <Clock className="w-3.5 h-3.5 text-muted-foreground" />
                <span className="text-xs font-medium text-foreground">
                  {Math.floor(maxSessionDuration / 3600)}h {Math.floor((maxSessionDuration % 3600) / 60)}m
                </span>
              </div>
            </div>
          )}

          {/* Inline Policies */}
          {inlinePolicyNames.length > 0 && (
            <div className="space-y-1.5">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                Inline Policies
              </p>
              <div className="flex flex-wrap gap-1.5">
                {inlinePolicyNames.map((policy: string, idx: number) => (
                  <Badge
                    key={idx}
                    variant="outline"
                    className="text-[10px] px-2 py-0.5 border-orange-500/30 bg-orange-500/10 text-orange-600 dark:text-orange-400"
                  >
                    <Shield className="w-2.5 h-2.5 mr-1" />
                    {policy}
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* Azure: Identity Name and Resource Group */}
      {isAzure && (
        <>
          <div className="space-y-1.5">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
              Identity Name
            </p>
            <div className="flex items-center gap-2">
              <Fingerprint className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
              <span className="text-xs font-medium text-foreground break-all flex-1">
                {saName}
              </span>
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6 hover:bg-muted/50 shrink-0"
                onClick={() => navigator.clipboard.writeText(saName)}
                aria-label="Copy name"
              >
                <Copy className="w-3 h-3" />
              </Button>
            </div>
          </div>

          {resourceGroup && (
            <div className="space-y-1.5">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                Resource Group
              </p>
              <div className="flex items-center gap-2">
                <Building className="w-3.5 h-3.5 text-muted-foreground" />
                <span className="text-xs font-mono bg-muted/50 px-2 py-0.5 rounded border border-border/30 break-all">
                  {resourceGroup}
                </span>
              </div>
            </div>
          )}

          {region && (
            <div className="space-y-1.5">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                Region
              </p>
              <div className="flex items-center gap-2">
                <MapPin className="w-3.5 h-3.5 text-muted-foreground" />
                <span className="text-xs font-medium text-foreground">
                  {region}
                </span>
              </div>
            </div>
          )}

          {subscriptionId && (
            <div className="space-y-1.5">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                Subscription
              </p>
              <div className="flex items-center gap-2">
                <Network className="w-3.5 h-3.5 text-muted-foreground" />
                <span className="text-xs font-mono bg-muted/50 px-2 py-0.5 rounded border border-border/30 break-all">
                  {subscriptionId}
                </span>
              </div>
            </div>
          )}
        </>
      )}

      {/* Activity */}
      <div className="space-y-1.5">
        <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
          Activity
        </p>
        <div className="flex items-center gap-2">
          <Activity className={`w-3.5 h-3.5 ${getActivityColor(activity)}`} />
          <span className={`text-xs font-semibold ${getActivityColor(activity)}`}>
            {activity}%
          </span>
          {lastActivityMessage && (
            <span className="text-xs text-muted-foreground">
              ({lastActivityMessage})
            </span>
          )}
        </div>
      </div>

      {/* Last Activity */}
      {lastActivity && (
        <div className="space-y-1.5">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
            Last Activity
          </p>
          <div className="flex items-center gap-2">
            <Clock className="w-3.5 h-3.5 text-muted-foreground" />
            <span className="text-xs font-medium text-foreground">
              {formatDate(lastActivity)}
            </span>
          </div>
        </div>
      )}

      {/* Project (GCP only) */}
      {projectId && !isAzure && !isAws && (
        <div className="space-y-1.5">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
            Project
          </p>
          <div className="flex items-center gap-2">
            <Building className="w-3.5 h-3.5 text-muted-foreground" />
            <span className="text-xs font-mono bg-muted/50 px-2 py-0.5 rounded border border-border/30 break-all">
              {projectId}
            </span>
          </div>
        </div>
      )}

      {/* Tenant */}
      {tenantName && (
        <div className="space-y-1.5">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
            Tenant
          </p>
          <div className="flex items-center gap-2">
            <Building className="w-3.5 h-3.5 text-muted-foreground" />
            <span className="text-xs font-medium text-foreground">
              {tenantName}
            </span>
          </div>
        </div>
      )}

      {/* Synced At */}
      {syncedAt && (
        <div className="space-y-1.5">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
            Last Synced
          </p>
          <div className="flex items-center gap-2">
            <Calendar className="w-3.5 h-3.5 text-muted-foreground" />
            <span className="text-xs font-medium text-foreground">
              {formatDate(syncedAt)}
            </span>
          </div>
        </div>
      )}

      <Separator className="bg-border/50" />

      {/* Roles Preview */}
      {roles.length > 0 && (
        <div className="space-y-2">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
            Assigned Roles
          </p>
          <div className="flex flex-wrap gap-1.5">
            {roles.slice(0, 5).map((roleId: string, idx: number) => {
              // Use roleNames map, fallback to formatted role ID
              const displayName = roleNames[roleId] || (() => {
                const suffix = roleId.split('/').pop() || roleId;
                return suffix.charAt(0).toUpperCase() + suffix.slice(1).replace(/([A-Z])/g, ' $1').trim();
              })();
              return (
                <Badge
                  key={idx}
                  variant="outline"
                  className="text-[10px] px-2 py-0.5 border-border/50 bg-muted/30"
                >
                  {displayName}
                </Badge>
              );
            })}
            {roles.length > 5 && (
              <Badge
                variant="outline"
                className="text-[10px] px-2 py-0.5 border-border/50 bg-muted/30"
              >
                +{roles.length - 5} more
              </Badge>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
